import type { SerializeFrom } from '~/types/serialize-from';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type JSX,
} from 'react';
import {
    Outlet,
    isRouteErrorResponse,
    useLocation,
    useMatches,
    useNavigation,
    useRouteError,
    useFetcher,
    useNavigate,
} from 'react-router';
import { useLatest, useDebounceFn } from 'ahooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { CatchPage, ServerErrorPage } from '@modules/error';
import { reloadDocument } from '@modules/error/util/document-navigation';
import { trackRouterTransition } from '~/modules/error/util/router-transition-origin';
import { useLogger } from '@archipro-website/logger/client';
import {
    makePageViewTrackingData,
    useLogPageView,
    useTracker,
    useLogLinkClicks,
} from '@archipro-website/tracker';
import { tileSliderAtom } from '@rocco/states/tileSliderAtom';
import {
    uiContextAtomArticleViewTransitionsEnabled,
    uiContextAtomViewTransitionsEnabled,
} from '@rocco/states/uiContextAtomViewTransitions';
import { useArticleRevampEnabled } from '@modules/article-revamp/util/is-article-revamp-enabled';
import { CartPopupProvider } from '@modules/ecommerce/context/CartPopupContext';
import { useHideNavigation } from '@modules/root/hook/use-hide-navigation';
import { useScrolledPastFirstScreen } from '@modules/article-revamp/hook/use-scrolled-past-first-screen';
import { useWarningMessages } from '@modules/root/hook/use-warning-messages';
import {
    useAppData,
    useContentClassName,
    useHeaderHandle,
    useMatchesHandleData,
    useMaybeAppData,
    useRootData,
    getServerVersion,
    useFeature,
} from '@modules/root';
import { HomeFooter } from '@ui-rocco/pages/home-page/sections/HomeFooter';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import { useOneTapSignIn } from '~/modules/user/hook/use-one-tap-sign-in';
import CountrySuggestModal from '@modules/root/component/country-suggest-modal/CountrySuggestModal';
import SiteWideModals from '@modules/root/component/site-wide-modals/SiteWideModals';
import { KeyboardDebugReadout } from '@modules/root/component/KeyboardDebugReadout';
import { toast } from 'sonner';
import { FollowSelectorProvider } from '~/modules/moodboards/components';
import { useOnObjectChange } from '@archipro-website/react-hooks';
import { useDeploymentShouldReload } from '@archipro-website/react-hooks';
import { Flex, ImageConfigProvider, useStyles } from '@archipro-design/aria';
import * as S from '../component/app-page-layout/AppPageLayout.style';
import { imageConfig } from '~/modules/images';
import { useAuthEvent } from '../hook/use-auth-event';
import { AuthContextProvider } from '~/modules/root/context';
import type { loader } from '~/routes/_app';
import type { GrowthBookSSRData } from '@growthbook/growthbook-react';
import { useGrowthBookSSR } from '@growthbook/growthbook-react';
import type { HeaderCartInfo } from '@rocco/ui/website-header/components/Header';
import { Header } from '@rocco/ui/website-header/components/Header';
import { AdvertTopBanner } from '@modules/adverts/component/advert-top-banner/AdvertTopBanner';
import { useTopBannerOffset } from '@modules/adverts/hook/use-top-banner-offset';
import {
    DESKTOP_NAV,
    DESKTOP_NAV_LEGACY,
    MOBILE_HEADER_PRIMARY_NAV,
    MOBILE_HEADER_PRIMARY_NAV_LEGACY,
    MOBILE_HEADER_SECONDARY_NAV,
    MOBILE_HEADER_SECONDARY_NAV_LEGACY,
    getMobileHeaderSocialNav,
} from '~/utils/route-config';
import { placeCountryDomain } from '~/utils/urlHelper';
import { getDomain } from '~/utils/get-domain';
import {
    countriesList,
    type SupportedCountry,
} from '@archipro-website/localisation';
import { getUserHeaderData } from '../util/get-user-header-data';
import { useLogout } from '~/modules/user/hook/use-logout';
import {
    pathsThatShouldRedirectToHome,
    buildCountryLink,
    stripLocationFromCountrySwitchPath,
} from '@archipro-website/top-navigation/src/lib/utils/country-link';
import {
    usePageTheme,
    useHeaderSolidTheme,
} from '~/modules-rocco/pages/hooks/use-page-theme';
// import { useAnimatedFavicon } from '@rocco/ui/favicon/hook/use-animated-favicon';
import { Toaster as RoccoToaster } from '@rocco/components/sonner';
import { SocialShareInitializer } from '~/modules/social-sharing/components/SocialShareInitializer';
import { MobileSearchModal } from '~/modules-rocco/search/page/mobile-search-modal-flow';
import { ClientOnly } from 'remix-utils';
import { useSetLoginModal } from '~/modules-rocco/user/hook/use-open-login-modal';
import { useEnquiryAutoSubmit } from '~/modules/enquiry/hooks/use-enquiry-auto-submit';
import { uiStateAtomAddProfessionalModalOpened } from '~/modules-rocco/root/states/ui-state-add-professional-modal-opened';
import { footerElementAtom } from '~/state/project-detail-states';
import { footerSuppressedAtom } from '~/state/ui-state-footer';
import { uiStateAtomSearchModalOpened } from '~/modules-rocco/search/states/ui-state-search-modal-opened';
import {
    addSavedPinKeys,
    makePinKey,
    savedPinKeysAtom,
} from '~/modules/moodboards/state/design-board-save-state';
import { DownloadGateModal } from '~/modules-rocco/product/component/DownloadGateModal';
import { CartDrawer } from '~/ui-rocco/components/cart/CartDrawer';
import { EnquiryModal } from '~/modules/enquiry/component/EnquiryModal';
import { cn } from '@rocco/utils/cn';
import type { HeaderCountryElement } from '~/utils/country-switch';
import { getCountrySwitchUrl } from '~/utils/country-switch';
import { getCloneCountryStateAtom } from '@archipro-website/web/app/state/ui-state-clone-country-link';
import { useAdProfessional } from '~/modules/tracking/hook/useAdProfessional';
import { useEmailAttribution } from '~/modules/tracking/hook/use-email-attribution';
import { getPermissions, useBatchLoadUserActiveStatuses } from '~/modules/user';
import {
    useNotifications,
    useNotificationTitleBadge,
} from '~/modules/notifications';
import { PermissionEnum } from 'generated/graphql';
import {
    useChatBoxes,
    useChatGlobals,
    useContactSuggestionsCache,
    useConversations,
    useTypingIndicatorManager,
} from '~/modules/chat';
import { ChatBoxes } from '~/modules/chat/components/ChatBoxes';
import {
    SiteFeedbackButton,
    useSiteFeedbackEnabled,
} from '~/modules/site-feedback';
import { StaffFeedbackIsland } from '~/modules/business-manager/component/detail-page-buttons/StaffFeedbackIsland';
import { useSalesMode } from '../hook/use-sales-mode';
import { useSearch } from '~/modules/search/hook/use-search';
import type { SearchSuggestionResultGroup } from '@rocco/ui/search/view-models/search-suggestion';
import { useSearchTracking } from '@ui-rocco/hooks/use-search-tracking';
import { removeHDEFromNavLinks } from './hde-country-gating';
import { useInlineWarningBanner } from '~/ui-rocco/pages/details-page/revamp/hooks/use-inline-warning-banner';
import { WarningBanner as RoccoWarningBanner } from '@rocco/ui/warning-banner/components/WarningBanner';

const AppProviders = ({ children }: { children: React.ReactNode }) => {
    return (
        <CartPopupProvider>
            <FollowSelectorProvider>{children}</FollowSelectorProvider>
            {/* Mounted here rather than in AppLayout so toasts render on every
                path that uses these providers — including the ErrorBoundary
                branch that has no app data and skips AppLayout entirely. */}
            <RoccoToaster />
        </CartPopupProvider>
    );
};

export type AppData = SerializeFrom<typeof loader>;

export const links = () => [
    {
        rel: 'stylesheet',
        href: '/assets/website/styles/swiper@9.1.0/swiper-bundle.min.css',
    },
];

/**
 * renders top nav and footer around its children
 * It requires app loader to return data.
 * @param children
 * @param data
 * @constructor
 */
const AppLayout = ({
    children,
    data,
}: {
    children: JSX.Element;
    data: AppData;
}) => {
    const config = useConfigContext();
    const location = useLocation();
    const matches = useMatches();
    const warningMessages = useWarningMessages();
    const setFooterElement = useSetAtom(footerElementAtom);
    // A closing section that turns into the next page claims the document's
    // end while it stands; the footer returns the moment it goes.
    const footerSuppressed = useAtomValue(footerSuppressedAtom);
    /**
     * Publish the footer as it mounts and unmounts.
     *
     * A callback ref rather than a mount effect: the footer renders
     * conditionally, and an effect that reads the ref once captures whatever
     * the first commit happened to contain. On a page whose closing section
     * suppresses the footer, that is the footer — which the very next render
     * removes, leaving the atom holding a detached node for the life of the
     * page. A detached element measures as a zero rect at the top of the
     * viewport, so every surface bounding itself against the footer (the
     * magic island, for one) believes it has reached the end of the page and
     * stays hidden. React calls this with null on unmount, so the atom can
     * only ever hold a footer that is on screen.
     */
    const footerRef = useCallback(
        (node: HTMLDivElement | null) => setFooterElement(node),
        [setFooterElement],
    );

    // useAnimatedFavicon();

    /**
     * we use useMatches here because we cannot call useLoaderData inside
     * Error boundaries. Since we are can't guarantee that we do have app data
     * we use the optional version
     */
    const {
        shoppingCartSummary,
        user,
        toastMessage,
        moodBoardPageIds,
        webSocketUrl,
        topBannerAd,
    } = data;

    const isLoggedIn = user.__typename === 'Me';
    const showTextLogoOnly =
        location.pathname === '/' || location.pathname === '';

    const isBMPreview = useMatchesHandleData('isBMPreview', false);
    const isPreviewFrame = useMatchesHandleData('isPreviewFrame', false);
    const buildHubEnabled = useFeature('buildhub');
    const eventTicketingEnabled = useFeature('event_ticketing');
    const newChatUI2025Enabled = useFeature('new_chat_ui_2025');
    // The consolidated revamp switch: detail/category revamps, view
    // transitions, tile refresh, button consistency, Get Matched homepage,
    // auth modal, subscribe modal and the rest ride this one flag.
    const revampEnabled = useFeature('revamp_2026');
    const siteFeedbackEnabled = useSiteFeedbackEnabled();
    const getMatchedRequestsOnlineEnabled = useFeature(
        'get_matched_requests_online',
    );
    const disableTileSliderProduct = useFeature('disable_tile_slider_product');
    const disableTileSliderProject = useFeature('disable_tile_slider_project');
    const productsPageTopnavDec2025Enabled = useFeature(
        'products_page_topnav_dec_2025',
    );
    const topBannerEnabled = useFeature('top_ad_banner');
    // Listing→detail view transitions. On `_app` (not `root`) so print
    // routes never call useFeature. Dev ?transitions=1 latches in
    // sessionStorage across navigations and full reloads.
    const viewTransitionsDevLatch = useRef(false);
    if (import.meta.env.DEV && typeof window !== 'undefined') {
        if (new URLSearchParams(location.search).get('transitions') === '1') {
            viewTransitionsDevLatch.current = true;
            window.sessionStorage.setItem('ap-transitions-dev', '1');
        } else if (
            window.sessionStorage.getItem('ap-transitions-dev') === '1'
        ) {
            viewTransitionsDevLatch.current = true;
        }
    }
    const viewTransitionsEnabled =
        revampEnabled || viewTransitionsDevLatch.current;
    const setViewTransitionsEnabled = useSetAtom(
        uiContextAtomViewTransitionsEnabled,
    );
    setViewTransitionsEnabled(viewTransitionsEnabled);
    // Article tiles additionally need the article detail revamp on: the
    // legacy article page has no `article-hero` counterpart, so an article
    // claim without the revamp cross-fades the whole page instead of
    // morphing (see the atom's doc comment).
    const articleRevampEnabled = useArticleRevampEnabled();
    const setArticleViewTransitionsEnabled = useSetAtom(
        uiContextAtomArticleViewTransitionsEnabled,
    );
    setArticleViewTransitionsEnabled(
        viewTransitionsEnabled && articleRevampEnabled,
    );
    useEffect(() => {
        document.documentElement.toggleAttribute(
            'data-view-transitions',
            viewTransitionsEnabled,
        );
        return () => {
            document.documentElement.removeAttribute('data-view-transitions');
        };
    }, [viewTransitionsEnabled]);

    /**
     * The tile refresh: the 32px corner, and the action panel that replaces the
     * board dropdown. One flag for both, because they are one design change and
     * a tile that rounds its corners without gaining its actions is neither.
     *
     * Carried as an attribute rather than a prop because `rounded-tile` is
     * named in 54 files; the token reads `--radius-tile`, so this moves all of
     * them. `TileOverlayWithFollowSelector` reads the same flag for the panel.
     */
    useEffect(() => {
        document.documentElement.toggleAttribute(
            'data-tile-refresh',
            revampEnabled,
        );
        return () => {
            document.documentElement.removeAttribute('data-tile-refresh');
        };
    }, [revampEnabled]);
    /**
     * The button corner ramp. The squared corner is part of the revamp design
     * language, so it rides the same flag as the surfaces that use it.
     *
     * An attribute for the same reason as the tile corner above — the ramp is
     * named across ~117 files and reads from `--radius-btn-*`, so this moves
     * all of them at once. With it off every token is 9999px, which is what
     * `rounded-full` drew.
     */
    useEffect(() => {
        document.documentElement.toggleAttribute(
            'data-button-consistency',
            revampEnabled,
        );
        return () => {
            document.documentElement.removeAttribute('data-button-consistency');
        };
    }, [revampEnabled]);

    const showTopBanner = topBannerEnabled && !!topBannerAd;
    const contentClassName = useContentClassName();
    const headerHandle = useHeaderHandle();

    const {
        env,
        config: rootConfig,
        growthbookContext,
        user: trackedUser,
    } = useRootData();
    const deployNotificationEnabled =
        rootConfig.deployNotification?.enabled ?? false;

    const logger = useLogger();
    const tracker = useTracker();
    const { onLogoutClick } = useLogout();
    const { notifications } = useNotifications({ global: true });
    const { isSearching } = useSearch();
    const setTileSlider = useSetAtom(tileSliderAtom);
    const { isChatBoxesDisabled } = useChatBoxes();
    useChatGlobals({ global: true });
    // The Forms editor's preview frame draws one form; it has no chat to keep
    // live and no one to sign in.
    useConversations({
        global: true,
        isDisabled: !newChatUI2025Enabled || isPreviewFrame,
    });
    useNotificationTitleBadge(!newChatUI2025Enabled);
    useContactSuggestionsCache(!newChatUI2025Enabled);
    useTypingIndicatorManager();
    useGrowthBookSSR(growthbookContext as GrowthBookSSRData);
    useOneTapSignIn(config.google.otsId, isBMPreview || isPreviewFrame);
    useBatchLoadUserActiveStatuses();

    // Fires page view events
    useLogPageView(location, tracker, (state) =>
        makePageViewTrackingData(state),
    );

    // Remember the professional whose ad brought the visitor here, so their
    // conversions can be credited to that client.
    useAdProfessional();

    // Remember the email link that brought the visitor here (e.g. a Get Matched
    // response), so a later enquiry can be attributed to it.
    useEmailAttribution();

    // Fire website referral & download tracking events
    useLogLinkClicks(location, tracker, logger, trackedUser);

    useEnquiryAutoSubmit();

    useAuthEvent(tracker);

    useOnObjectChange(
        {
            hashKey: 'feature-flags',
            value: {
                user: {
                    id: user.ID,
                    type: user.__typename,
                },
                features: user.FeatureFlags.map(({ Name, Enabled }) => {
                    return { Name, Enabled };
                }),
            },
            callback: () => {
                reloadDocument();
            },
        },
        [user],
    );

    const { topNavigation, footerNavigation } = useHideNavigation(matches);
    // The unified header is now the default site-wide. Routes can
    // optionally provide a `header` handle for per-page tagline / CTAs /
    // colour variant. When the handle is present we also bypass
    // `hideTopNavigation` so the v2 header renders on routes that previously
    // opted out via the legacy hide handle.
    const renderTopNavigation = topNavigation || !!headerHandle;

    // Pages whose first screen is a full-bleed masthead ask for the floating
    // bars to arrive together, a screen down, rather than one of them sitting
    // on the picture from load. The page's own bars latch themselves; this is
    // the shell's half, for the messaging bar it owns.
    const islandsEnterOnScroll = matches.some(
        (match) =>
            (match.data as { islandsEnterOnScroll?: boolean } | null)
                ?.islandsEnterOnScroll === true,
    );
    const scrolledPastFirstScreen = useScrolledPastFirstScreen();

    useEffect(() => {
        if (!toastMessage) return;
        if (toastMessage.type === 'error') {
            toast.error(toastMessage.message);
        } else if (toastMessage.type === 'success') {
            toast.success(toastMessage.message);
        } else {
            toast(toastMessage.message);
        }
    }, [toastMessage]);

    const shouldReload = useDeploymentShouldReload({
        clientVersion: env.version,
        host: env.host,
        getServerVersion,
        webSocketUrl,
        enabled: deployNotificationEnabled,
    });
    const navigation = useNavigation();
    const shouldReloadRef = useLatest(shouldReload);

    const countryData = useAtomValue(getCloneCountryStateAtom);
    const inlineWarningBanner = useInlineWarningBanner();

    // Remember whether this transition was a form action so CatchPage can skip
    // Cloudflare auto-reload after navigation returns to idle with an ErrorResponse.
    useEffect(() => {
        trackRouterTransition({
            state: navigation.state,
            formMethod: navigation.formMethod,
        });
    }, [navigation.state, navigation.formMethod]);

    useEffect(() => {
        if (
            navigation.state === 'loading' &&
            navigation.location &&
            shouldReloadRef.current()
        ) {
            window.location.replace(
                `${navigation.location.pathname}${navigation.location.search}`,
            );
        }
    }, [navigation, shouldReloadRef]);

    const { appDisplayMode, localisation } = useRootData();
    const isMobile = appDisplayMode === 'mobile';
    const currentCountryCode = localisation.country; // FIXME
    const { countryConfig } = localisation;
    const domain = getDomain(currentCountryCode);
    const isChatBoxEnabled =
        newChatUI2025Enabled &&
        !isChatBoxesDisabled &&
        isLoggedIn &&
        !isMobile &&
        !isBMPreview &&
        (!islandsEnterOnScroll || scrolledPastFirstScreen);

    const pathParts = location.pathname.split('/').filter(Boolean) ?? [];
    const shouldRedirectHome = pathsThatShouldRedirectToHome.includes(
        pathParts[0] ?? '',
    );

    const pathAndSearch = shouldRedirectHome
        ? ''
        : stripLocationFromCountrySwitchPath(
              (location.pathname ?? '') + (location.search ?? ''),
          );

    // All supported markets (NZ, AU, US) are now publicly live, so every market
    // shows the full country switcher list — no per-site filtering.
    const countries = Object.values(countriesList).map((country) => {
        const targetCountryCode = country.shortName as SupportedCountry;

        const countryElement: HeaderCountryElement = {
            code: country.shortName,
            name: country.name,
            url:
                buildCountryLink(env, currentCountryCode, targetCountryCode) +
                pathAndSearch,
        };
        return {
            ...countryElement,
            url: getCountrySwitchUrl(
                countryElement,
                countryData,
                location.pathname,
            ),
        };
    });

    const isGetMatchedAdmin =
        user.__typename === 'Me' && Boolean(user.CanAccessGetMatchedAdmin);

    const { headerUserData, profileLinks } = getUserHeaderData(
        user,
        notifications,
        buildHubEnabled,
        currentCountryCode,
        eventTicketingEnabled,
        newChatUI2025Enabled,
        {
            allowMemberGuides: countryConfig.allowMemberGuides,
            allowMemberOrders: countryConfig.allowMemberOrders,
            allowMemberTickets: countryConfig.allowMemberTickets,
        },
        isGetMatchedAdmin,
        revampEnabled,
        getMatchedRequestsOnlineEnabled,
    );
    const currentUrl = location.pathname + location.search;

    const pageTheme = usePageTheme();
    // Loader-driven solid header theme (e.g. the project revamp's dark bar),
    // falling back to the route handle's static `solidTheme`.
    const loaderSolidTheme = useHeaderSolidTheme();
    const [currentPageTheme, setCurrentPageTheme] = useState(pageTheme);
    // Routes that pin a transparent-overlay header own their own scroll
    // transition (the MobileHeader switches itself to solid past the hero), so
    // the scroll-driven flip to `default` here only adds the mobile nav-clearance
    // padding mid-scroll — a layout shift that fights slow scrolling on the
    // full-bleed hero. Skip it for those routes and keep the page theme stable.
    const ownsHeroTransition = headerHandle?.headerVariant === 'transparent';
    useEffect(() => {
        setCurrentPageTheme(pageTheme);
        if (!isMobile || pageTheme === 'none' || ownsHeroTransition) return;
        const toggleTheme = () => {
            if (window.scrollY > 100) {
                setCurrentPageTheme('default');
            } else {
                setCurrentPageTheme(pageTheme);
            }
        };
        toggleTheme();
        window.addEventListener('scroll', toggleTheme);
        return () => {
            window.removeEventListener('scroll', toggleTheme);
        };
    }, [currentUrl, pageTheme, isMobile, ownsHeroTransition]);

    const { css } = useStyles({ currentPageTheme });
    const [, setSearchModalOpened] = useAtom(uiStateAtomSearchModalOpened);

    const { setLoginModal } = useSetLoginModal();
    const setAddProfessionalModalOpened = useSetAtom(
        uiStateAtomAddProfessionalModalOpened,
    );

    const setSavedPinKeys = useSetAtom(savedPinKeysAtom);
    const hasInitialisedSavedPins = useRef(false);

    useEffect(() => {
        if (hasInitialisedSavedPins.current) {
            return;
        }

        hasInitialisedSavedPins.current = true;

        if (!moodBoardPageIds) {
            setSavedPinKeys(new Set());
            return;
        }

        const pageKeys = moodBoardPageIds.pageIDs.map((id) =>
            makePinKey(id, 'page'),
        );
        const imageKeys = moodBoardPageIds.imageIDs.map((id) =>
            makePinKey(id, 'image'),
        );

        setSavedPinKeys(
            addSavedPinKeys(new Set(), [...pageKeys, ...imageKeys]),
        );
    }, [moodBoardPageIds, setSavedPinKeys]);

    const canApproveContent = getPermissions(
        user,
        PermissionEnum.BmContentApprove,
    );
    const canCreateProfessional = getPermissions(
        user,
        PermissionEnum.BmProfessionalCreate,
    );

    const { canSeeSalesData, isSalesMode, toggleSalesMode } = useSalesMode(
        isLoggedIn ? user : null,
    );

    const additionalUserMenuItems =
        canCreateProfessional || canApproveContent
            ? {
                  links: [
                      ...(canCreateProfessional
                          ? [
                                {
                                    title: 'Add Professional',
                                    to: '#',
                                    onClick: () => {
                                        setAddProfessionalModalOpened(true);
                                    },
                                },
                            ]
                          : []),
                      ...(canApproveContent
                          ? [
                                {
                                    title: 'Content Hub',
                                    to: '/ap-admin/content/projects',
                                    external: true,
                                },
                            ]
                          : []),
                      ...(canSeeSalesData
                          ? [
                                {
                                    title: isSalesMode
                                        ? 'Exit Sales Admin'
                                        : 'Enter Sales Admin',
                                    to: '#',
                                    onClick: () => {
                                        toast.success(
                                            isSalesMode
                                                ? 'Sales mode disabled.'
                                                : 'Welcome to Sales Admin.',
                                        );
                                        toggleSalesMode();
                                    },
                                },
                            ]
                          : []),
                  ],
              }
            : undefined;

    const onMobileSearchClick = () => {
        setSearchModalOpened(true);
    };

    // Same rule as the phone's set below: the revamp renames the fourth link
    // to Publications, so the flag chooses the link set as well as the bar
    // that draws it. Otherwise the rename reaches the public ahead of the
    // design — which it did, on every desktop page, flag off.
    const primaryDesktopLinks = revampEnabled
        ? DESKTOP_NAV
        : DESKTOP_NAV_LEGACY;
    const countryAwareDesktopLinks = removeHDEFromNavLinks(
        primaryDesktopLinks,
        countryConfig.showHDENavLink,
    );
    // The phone menu is relabelled by the revamp and loses About us, so the
    // flag has to choose the link set as well as the bar that draws it.
    // Otherwise the navigation change ships to the public ahead of the design.
    const countryAwareSecondaryMobileLinks = removeHDEFromNavLinks(
        revampEnabled
            ? MOBILE_HEADER_SECONDARY_NAV
            : MOBILE_HEADER_SECONDARY_NAV_LEGACY,
        countryConfig.showHDENavLink,
    );
    const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
    const shoppingCart = shoppingCartSummary?.ShoppingCart;
    const cartInfo: HeaderCartInfo = {
        items:
            shoppingCart?.LastAddedItems?.map((el) => ({
                id: el.ID,
                title: el.Variant?.Product?.Title,
                price: el.Price.ShortFormat,
                quantity: el.Quantity,
                isSample: el.Sample,
                thumbnail: {
                    desktop: el.Variant?.Thumbnail?.Desktop ?? '',
                    mobile: el.Variant?.Thumbnail?.Mobile ?? '',
                },
            })) || [],
        itemTotal: {
            ShortFormat: shoppingCart?.ItemTotal?.ShortFormat || '',
            Amount: shoppingCart?.ItemTotal?.Amount || 0,
        },
        cartCount: notifications?.cart?.count ?? 0,
    };

    const onNavigate = useNavigate();

    // Header search tracking configuration
    const headerSearchTrackingConfig = useSearchTracking();

    // Header search suggestions fetcher
    const headerSearchSuggestionsFetcher =
        useFetcher<SearchSuggestionResultGroup | null>();

    // Fetch suggestions when user types in header search (debounced)
    const { run: fetchHeaderSearchSuggestions } = useDebounceFn(
        (query: string) => {
            const params = new URLSearchParams({ search: query.trim() });
            headerSearchSuggestionsFetcher.load(
                `/actions/search/search-suggestions?${params.toString()}`,
            );
        },
        { wait: 50 }, // keep it as low as possible to avoid feel lag
    );

    const isHighAdmin =
        trackedUser?.__typename === 'Me' &&
        'IsHighAdmin' in trackedUser &&
        trackedUser.IsHighAdmin === true;

    useEffect(() => {
        // Enable/disable tile sliders based on the website settings.
        // BC-4449: the `tile-slider-disabled` A/B gate has been removed. The
        // product slider is controlled solely by its CMS feature flag, while the
        // project slider is additionally restricted to ArchiPro high admins.
        setTileSlider((state) => ({
            ...state,
            enableTileSliderProduct: !disableTileSliderProduct,
            enableTileSliderProject: !disableTileSliderProject && isHighAdmin,
        }));
    }, [
        disableTileSliderProduct,
        disableTileSliderProject,
        isHighAdmin,
        setTileSlider,
    ]);

    // avoid duplicate scrollbar in iframe in BM profile preview
    const isIframe = useMemo(() => {
        if (typeof window === 'undefined') return false;
        return window.self !== window.top;
    }, []);
    const pageRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (isIframe && pageRef.current) {
            pageRef.current.style.overflow = location.pathname.startsWith(
                '/professional-preview/',
            )
                ? 'visible'
                : '';
        }
    }, [isIframe, pageRef, location.pathname]);

    // Site-wide top ad banner: measure its height and drive the CSS vars the
    // header reads so the fixed/absolute header is pushed below it.
    const bannerRef = useRef<HTMLDivElement>(null);
    useTopBannerOffset(bannerRef, pageRef, showTopBanner, topBannerAd?.id);

    return (
        <ImageConfigProvider value={imageConfig}>
            <AuthContextProvider>
                <Flex
                    ref={pageRef}
                    id="app-page-layout"
                    column
                    className={cn(css(S.PageBase))}
                >
                    {/* First tab stop on every page: visually hidden until
                        keyboard-focused, then overlays the top-left corner. */}
                    <a
                        href="#app-page-content"
                        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[10000] focus:bg-white focus:px-4 focus:py-3 focus:font-sans focus:text-base focus:text-[#181818] focus:shadow-md"
                    >
                        Skip to main content
                    </a>
                    {showTopBanner && topBannerAd ? (
                        <>
                            {/* SSR defaults for the header offset vars so the
                                fixed/absolute header sits below the banner on
                                first paint, before the client hook measures the
                                exact height. Matches the banner band's reserved
                                min-heights (3.75rem mobile / 7.5rem desktop); the
                                hook then overrides via inline style. Scoped so
                                banner-less pages keep the 0px fallback. */}
                            <style
                                dangerouslySetInnerHTML={{
                                    __html:
                                        '#app-page-layout{--ad-top-banner-height:100px;--ad-top-banner-offset:100px}' +
                                        '@media(min-width:768px){#app-page-layout{--ad-top-banner-height:231px;--ad-top-banner-offset:231px}}',
                                }}
                            />
                            <div ref={bannerRef} className="relative w-full">
                                <AdvertTopBanner ad={topBannerAd} />
                            </div>
                        </>
                    ) : null}
                    {/* BC-4705: the product detail revamp gets the warning
                        banner ABOVE the top nav so it never overlaps the nav's
                        logomark or the hero. `useInlineWarningBanner` derives
                        this from route matches, so server and client render the
                        banner in the same place (no post-hydration jump).
                        Desktop only — the mobile header is fixed, so it renders
                        the banner itself at the top of its strip. */}
                    {inlineWarningBanner &&
                    !isMobile &&
                    warningMessages?.length ? (
                        <RoccoWarningBanner alerts={warningMessages} />
                    ) : null}
                    {renderTopNavigation ? (
                        <Header
                            showTextLogoOnly={showTextLogoOnly}
                            cartInfo={cartInfo}
                            // On the revamp the cart opens beside the page
                            // rather than navigating away from it — the reader
                            // is usually mid-browse, and the cart page is a
                            // checkout, not a look.
                            //
                            // The legacy experience keeps the navigation it has
                            // always had. This control sits in the header of
                            // every page, revamped or not, so it cannot change
                            // on a flag the reader has not been given.
                            onUserCartClick={
                                revampEnabled
                                    ? () => setCartDrawerOpen(true)
                                    : () => {
                                          window.location.href =
                                              '/shopping-cart';
                                      }
                            }
                            // And where the click still navigates away, the
                            // hover is how the mini-cart is reached.
                            openCartMenuOnHover={!revampEnabled}
                            currentUrl={currentUrl}
                            userData={headerUserData}
                            currentCountryCode={currentCountryCode}
                            allowMemberGuides={countryConfig.allowMemberGuides}
                            allowMemberOrders={countryConfig.allowMemberOrders}
                            onNavigate={onNavigate}
                            countries={countries}
                            primaryDesktopLinks={countryAwareDesktopLinks}
                            profileMobileLinks={profileLinks}
                            revampMobileHeader={revampEnabled}
                            primaryMobileLinks={placeCountryDomain(
                                revampEnabled
                                    ? MOBILE_HEADER_PRIMARY_NAV
                                    : MOBILE_HEADER_PRIMARY_NAV_LEGACY,
                                domain,
                            )}
                            secondaryMobileLinks={placeCountryDomain(
                                countryAwareSecondaryMobileLinks,
                                domain,
                            )}
                            socialMobileLinks={
                                revampEnabled
                                    ? getMobileHeaderSocialNav(
                                          currentCountryCode,
                                      )
                                    : undefined
                            }
                            onLogout={onLogoutClick}
                            displayMode={appDisplayMode}
                            color={
                                headerHandle?.headerVariant ??
                                (currentPageTheme === 'transparent'
                                    ? 'transparent'
                                    : currentPageTheme === 'app'
                                      ? 'app'
                                      : currentPageTheme === 'none'
                                        ? 'none'
                                        : 'solid')
                            }
                            solidTheme={
                                loaderSolidTheme ?? headerHandle?.solidTheme
                            }
                            mode={'fixed'}
                            additionalUserMenuItems={additionalUserMenuItems}
                            isSearching={isSearching}
                            warningMessages={warningMessages}
                            onLoginClick={() => {
                                setLoginModal({
                                    authSource: 'topNav',
                                    isOpen: true,
                                });
                            }}
                            onMobileSearchClick={onMobileSearchClick}
                            fetchSuggestions={fetchHeaderSearchSuggestions}
                            suggestionData={headerSearchSuggestionsFetcher.data}
                            headerSearchTrackingConfig={
                                headerSearchTrackingConfig
                            }
                            productsPageTopnavDec2025Enabled={
                                productsPageTopnavDec2025Enabled
                            }
                            tagline={headerHandle?.tagline}
                            primaryCta={headerHandle?.primaryCta}
                            secondaryCta={headerHandle?.secondaryCta}
                            showLogomark={headerHandle?.showLogomark}
                            glassLogomark={headerHandle?.glassLogomark}
                            groundClassName={headerHandle?.ground}
                            secondaryNavLinks={
                                headerHandle?.secondaryNavLinks
                                    ? {
                                          links: headerHandle.secondaryNavLinks,
                                      }
                                    : undefined
                            }
                            suppressWarningBanner={inlineWarningBanner}
                        />
                    ) : null}
                    <div
                        className={cn(
                            css(S.PageContent),
                            location.pathname.includes('/project/')
                                ? 'bg-gray-50'
                                : '',
                            'app-page-content',
                            contentClassName,
                        )}
                        id="app-page-content"
                    >
                        {children}
                    </div>
                    {footerNavigation && !footerSuppressed ? (
                        <div ref={footerRef}>
                            {/* New site-wide footer (BC-4466), replacing the legacy footer.
                                Self-contained: sources country/HDE gating from
                                useLocalisation. Defaults to the dark theme. */}
                            <HomeFooter />
                        </div>
                    ) : null}
                </Flex>
                <CountrySuggestModal countries={countries} />
                <SiteWideModals />
                {/* `?keyboardDebug=1`, outside production only. */}
                <KeyboardDebugReadout />
                <SocialShareInitializer displayMode={appDisplayMode} />
                <EnquiryModal />
                <DownloadGateModal />
                {/* The cart, from the header's icon on any page. The same
                    panel the product drawer slides to after an add. Behind the
                    revamp with the control that opens it, so the legacy header
                    does not carry a drawer nothing can reach. */}
                {revampEnabled ? (
                    <CartDrawer
                        open={cartDrawerOpen}
                        onOpenChange={setCartDrawerOpen}
                        items={shoppingCart?.LastAddedItems ?? []}
                        subtotal={shoppingCart?.ItemTotal?.ShortFormat}
                    />
                ) : null}
                {isMobile && <MobileSearchModal />}
                {isChatBoxEnabled && (
                    <ClientOnly>{() => <ChatBoxes user={user} />}</ClientOnly>
                )}
                {/* Revamp beta feedback, for ArchiPro staff on the revamp. The gate owns both
                    conditions; `remix-api.site-feedback` re-checks staff, since a hidden button
                    is not a closed door. */}
                {/* On desktop the feedback control lives in the staff island,
                    bottom left: a detail page's own island carries it, and this
                    feedback-only one stands in everywhere else. Smaller screens
                    have no island, so they keep the floating pill. */}
                {siteFeedbackEnabled && !isPreviewFrame && (
                    <ClientOnly>
                        {() =>
                            appDisplayMode === 'desktop' ? (
                                <StaffFeedbackIsland />
                            ) : (
                                <SiteFeedbackButton />
                            )
                        }
                    </ClientOnly>
                )}
            </AuthContextProvider>
        </ImageConfigProvider>
    );
};

export const ErrorBoundary = () => {
    const data = useMaybeAppData();
    const error = useRouteError();

    if (isRouteErrorResponse(error)) {
        if (data) {
            return (
                <AppProviders>
                    <AppLayout data={data}>
                        <CatchPage caught={error} />
                    </AppLayout>
                </AppProviders>
            );
        }
        return (
            <AppProviders>
                <CatchPage caught={error} />
            </AppProviders>
        );
    }

    let errorObj = new Error('Unknown error');
    if (error instanceof Error) {
        errorObj = error;
    }

    return (
        <AppProviders>
            <ServerErrorPage error={errorObj} />
        </AppProviders>
    );
};

const AppPage = () => {
    const appData = useAppData();

    return (
        <AppProviders>
            <AppLayout data={appData}>
                <Outlet />
            </AppLayout>
        </AppProviders>
    );
};

export default AppPage;
